'use client';

import { useState, useMemo, useEffect } from 'react';
import Link from 'next/link';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';
import { saveToolScore } from '@/lib/tool-score-storage';

interface ArchitectureCategory {
  id: string;
  name: string;
  icon: string;
  criteria: { id: string; level: number; description: string }[];
}

const categories: ArchitectureCategory[] = [
  {
    id: 'business',
    name: 'Business Architecture',
    icon: '💼',
    criteria: [
      { id: 'b1', level: 1, description: 'No alignment between business and technology, reactive IT' },
      { id: 'b2', level: 2, description: 'Basic business requirements captured, minimal governance' },
      { id: 'b3', level: 3, description: 'Business capabilities mapped, architecture roadmap defined' },
      { id: 'b4', level: 4, description: 'Strong business-IT alignment, value stream mapping, KPIs tracked' },
      { id: 'b5', level: 5, description: 'Architecture drives business strategy, continuous value delivery' },
    ],
  },
  {
    id: 'design',
    name: 'Application Architecture',
    icon: '📐',
    criteria: [
      { id: 'd1', level: 1, description: 'No documented architecture, ad-hoc design, tightly coupled' },
      { id: 'd2', level: 2, description: 'Basic architecture documentation, layered monolith' },
      { id: 'd3', level: 3, description: 'Clear separation of concerns, documented patterns, API-first' },
      { id: 'd4', level: 4, description: 'Microservices/modular architecture with well-defined boundaries' },
      { id: 'd5', level: 5, description: 'Event-driven, self-healing, domain-driven design (DDD)' },
    ],
  },
  {
    id: 'data',
    name: 'Data Architecture',
    icon: '🗄️',
    criteria: [
      { id: 'data1', level: 1, description: 'Single database, no data governance, no backup strategy' },
      { id: 'data2', level: 2, description: 'Database replication, basic indexing, scheduled backups' },
      { id: 'data3', level: 3, description: 'Caching layer, read replicas, partitioning, data catalog' },
      { id: 'data4', level: 4, description: 'Polyglot persistence, event sourcing, CQRS, data lineage' },
      { id: 'data5', level: 5, description: 'Data mesh, real-time analytics, ML pipelines, data products' },
    ],
  },
  {
    id: 'technology',
    name: 'Technology Architecture',
    icon: '⚙️',
    criteria: [
      { id: 't1', level: 1, description: 'Legacy tech stack, manual deployments, no infrastructure as code' },
      { id: 't2', level: 2, description: 'Standardized platforms, basic CI/CD, scripted deployments' },
      { id: 't3', level: 3, description: 'Cloud-native, containerization, automated provisioning' },
      { id: 't4', level: 4, description: 'Multi-cloud, orchestration (K8s), GitOps, immutable infrastructure' },
      { id: 't5', level: 5, description: 'Platform engineering, developer self-service, policy as code' },
    ],
  },
  {
    id: 'scalability',
    name: 'Scalability & Performance',
    icon: '📈',
    criteria: [
      { id: 's1', level: 1, description: 'Monolithic, scaling issues at low load, no performance testing' },
      { id: 's2', level: 2, description: 'Vertical scaling only, manual intervention, basic load testing' },
      { id: 's3', level: 3, description: 'Horizontal scaling capability, load balancing, performance benchmarks' },
      { id: 's4', level: 4, description: 'Auto-scaling based on metrics, CDN, performance budgets' },
      { id: 's5', level: 5, description: 'Global distribution, multi-region, elastic at scale, chaos engineering' },
    ],
  },
  {
    id: 'reliability',
    name: 'Reliability & Resilience',
    icon: '🛡️',
    criteria: [
      { id: 'r1', level: 1, description: 'Frequent outages, no redundancy, no incident process' },
      { id: 'r2', level: 2, description: 'Basic backups, manual recovery, informal incident response' },
      { id: 'r3', level: 3, description: 'Redundancy, automated backups, defined RTO/RPO, SRE practices' },
      { id: 'r4', level: 4, description: 'Circuit breakers, retry logic, graceful degradation, error budgets' },
      { id: 'r5', level: 5, description: 'Chaos engineering, self-healing, 99.99%+ uptime, blameless postmortems' },
    ],
  },
  {
    id: 'security',
    name: 'Security & Compliance',
    icon: '🔒',
    criteria: [
      { id: 'sec1', level: 1, description: 'Minimal security, hardcoded credentials, no compliance' },
      { id: 'sec2', level: 2, description: 'Basic auth, HTTPS, simple firewall, awareness of compliance' },
      { id: 'sec3', level: 3, description: 'MFA, encrypted at rest/transit, regular audits, basic compliance' },
      { id: 'sec4', level: 4, description: 'Zero-trust, automated scanning, SIEM, compliance certifications (SOC2/ISO)' },
      { id: 'sec5', level: 5, description: 'Defense in depth, threat modeling, security champions, continuous compliance' },
    ],
  },
  {
    id: 'observability',
    name: 'Observability & Operations',
    icon: '👁️',
    criteria: [
      { id: 'o1', level: 1, description: 'No monitoring, logging, or alerting' },
      { id: 'o2', level: 2, description: 'Basic logging, simple alerts, manual troubleshooting' },
      { id: 'o3', level: 3, description: 'Centralized logging, metrics dashboards, alerting, on-call rotation' },
      { id: 'o4', level: 4, description: 'Distributed tracing, APM, SLO/SLI tracking, runbooks' },
      { id: 'o5', level: 5, description: 'Full observability stack, AI-powered anomaly detection, AIOps' },
    ],
  },
  {
    id: 'devops',
    name: 'DevOps & Deployment',
    icon: '🚀',
    criteria: [
      { id: 'do1', level: 1, description: 'Manual deployments, no CI/CD, long release cycles' },
      { id: 'do2', level: 2, description: 'Basic CI, scripted deployments, monthly releases' },
      { id: 'do3', level: 3, description: 'Full CI/CD pipeline, automated testing, weekly releases' },
      { id: 'do4', level: 4, description: 'GitOps, blue-green/canary deployments, daily releases, feature flags' },
      { id: 'do5', level: 5, description: 'Continuous deployment, progressive delivery, DORA metrics excellence' },
    ],
  },
  {
    id: 'governance',
    name: 'Architecture Governance',
    icon: '📋',
    criteria: [
      { id: 'g1', level: 1, description: 'No governance, no standards, no review process' },
      { id: 'g2', level: 2, description: 'Informal standards, ad-hoc reviews, basic documentation' },
      { id: 'g3', level: 3, description: 'Architecture review board (ARB), defined standards, ADRs' },
      { id: 'g4', level: 4, description: 'Automated compliance checks, architecture fitness functions, tech radar' },
      { id: 'g5', level: 5, description: 'Evolutionary architecture, continuous governance, architecture guild' },
    ],
  },
];

export default function ArchitectureMaturityAssessmentPage() {
  const [currentLevels, setCurrentLevels] = useState<Record<string, number>>(() => {
    const initial: Record<string, number> = {};
    categories.forEach(cat => {
      initial[cat.id] = 1;
    });
    return initial;
  });
  const [saved, setSaved] = useState(false);

  // Command Center integration
  const {
    enabled: commandCenterEnabled,
    saving,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'architecture-maturity-assessment',
    toolCategory: 'maturity',
  });

  const overallMaturity = useMemo(() => {
    const avg = Object.values(currentLevels).reduce((sum, val) => sum + val, 0) / Object.values(currentLevels).length;
    const level = Math.round(avg);

    const maturityLabels = [
      { level: 1, name: 'Initial', color: 'text-red-500', description: 'Ad-hoc, reactive approach' },
      { level: 2, name: 'Developing', color: 'text-accent', description: 'Basic practices in place' },
      { level: 3, name: 'Defined', color: 'text-yellow-500', description: 'Standardized and documented' },
      { level: 4, name: 'Managed', color: 'text-blue-400', description: 'Measured and controlled' },
      { level: 5, name: 'Optimizing', color: 'text-green-500', description: 'Continuous improvement' },
    ];

    return { ...maturityLabels[level - 1], avgScore: avg, level };
  }, [currentLevels]);

  useEffect(() => {
    saveToolScore('architecture-maturity-assessment', (overallMaturity.avgScore / 5) * 100);
  }, [overallMaturity.avgScore]);

  const recommendations = useMemo(() => {
    const recs: string[] = [];
    Object.entries(currentLevels).forEach(([catId, level]) => {
      if (level < 3) {
        const cat = categories.find(c => c.id === catId);
        if (cat) {
          recs.push(`Improve ${cat.name}: Currently at Level ${level}, aim for Level ${level + 1}`);
        }
      }
    });
    return recs.slice(0, 5);
  }, [currentLevels]);

  // Save to Command Center
  const handleSaveToCommandCenter = async () => {
    if (!commandCenterEnabled) return;

    const gaps = categories
      .filter(cat => currentLevels[cat.id] < 3)
      .map(cat => ({
        id: cat.id,
        title: `${cat.name} at Level ${currentLevels[cat.id]}`,
        severity: currentLevels[cat.id] === 1 ? 'critical' as const : 'high' as const,
        description: cat.criteria.find(c => c.level === currentLevels[cat.id])?.description || '',
      }));

    const recs = categories
      .filter(cat => currentLevels[cat.id] < 5)
      .sort((a, b) => currentLevels[a.id] - currentLevels[b.id])
      .slice(0, 5)
      .map(cat => ({
        id: cat.id,
        title: `Improve ${cat.name} to Level ${currentLevels[cat.id] + 1}`,
        priority: currentLevels[cat.id] < 2 ? 'high' as const :
                  currentLevels[cat.id] < 3 ? 'high' as const : 'medium' as const,
        description: cat.criteria.find(c => c.level === currentLevels[cat.id] + 1)?.description || '',
      }));

    const scores: Record<string, number> = {};
    categories.forEach(cat => {
      scores[cat.id] = (currentLevels[cat.id] / 5) * 100;
    });

    const assessment = await saveResult({
      name: `Architecture Maturity - ${new Date().toLocaleDateString()}`,
      score_overall: (overallMaturity.avgScore / 5) * 100,
      scores,
      answers: currentLevels,
      gaps,
      recommendations: recs,
    });

    if (assessment) {
      setSaved(true);
    }
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-4xl">🏛️</span>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground">
              Architecture Maturity Assessment
            </h1>
          </div>
          <p className="text-xl text-foreground-secondary">
            Evaluate your architecture practices across 10 TOGAF-aligned dimensions
          </p>
        </div>

        <nav className="mb-8 text-sm text-foreground-tertiary">
          <Link href="/" className="hover:text-accent transition-colors">Home</Link>
          <span className="mx-2">/</span>
          <Link href="/tools" className="hover:text-accent transition-colors">Tools</Link>
          <span className="mx-2">/</span>
          <span className="text-foreground">Architecture Maturity Assessment</span>
        </nav>

        <div className="bg-accent-muted border border-border rounded-lg p-6 mb-8">
          <h3 className="font-bold text-accent mb-2">How to use this assessment</h3>
          <ol className="text-foreground-secondary text-sm space-y-2 list-decimal list-inside">
            <li>For each dimension, select the level that best describes your current state</li>
            <li>Be honest - this is for your benefit</li>
            <li>Review your overall maturity level and category-specific scores</li>
            <li>Use recommendations to create an improvement roadmap</li>
          </ol>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {categories.map(category => (
              <div key={category.id} className="bg-surface rounded-lg border border-border p-6">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-3xl">{category.icon}</span>
                  <h3 className="text-xl font-bold text-foreground">{category.name}</h3>
                </div>

                <div className="space-y-3">
                  {category.criteria.map(criterion => (
                    <label
                      key={criterion.id}
                      className={`flex items-start gap-3 p-3 rounded cursor-pointer transition-colors ${
                        currentLevels[category.id] === criterion.level
                          ? 'bg-accent-muted border border-accent'
                          : 'bg-background border border-border-muted hover:border-border'
                      }`}
                    >
                      <input
                        type="radio"
                        name={category.id}
                        checked={currentLevels[category.id] === criterion.level}
                        onChange={() => setCurrentLevels({ ...currentLevels, [category.id]: criterion.level })}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="font-semibold text-foreground mb-1">
                          Level {criterion.level}
                        </div>
                        <div className="text-sm text-foreground-secondary">{criterion.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-6">
            <div className="bg-surface rounded-lg border border-border p-6 sticky top-4">
              <h3 className="text-xl font-bold text-foreground mb-4">Overall Maturity</h3>
              <div className="text-center py-6 mb-4">
                <div className="text-6xl font-bold text-accent mb-2">
                  {overallMaturity.level}
                </div>
                <div className={`text-2xl font-bold ${overallMaturity.color} mb-2`}>
                  {overallMaturity.name}
                </div>
                <div className="text-sm text-foreground-secondary">{overallMaturity.description}</div>
                <div className="mt-4 text-sm text-foreground-tertiary">
                  Average Score: {overallMaturity.avgScore.toFixed(2)}
                </div>
              </div>

              <div className="border-t border-border-muted pt-4 mb-4">
                <h4 className="font-semibold text-foreground mb-3">Category Levels:</h4>
                <div className="space-y-2">
                  {categories.map(cat => (
                    <div key={cat.id} className="flex justify-between items-center">
                      <span className="text-sm text-foreground-secondary flex items-center gap-2">
                        <span>{cat.icon}</span>
                        <span>{cat.name}</span>
                      </span>
                      <span className="text-accent font-bold">Level {currentLevels[cat.id]}</span>
                    </div>
                  ))}
                </div>
              </div>

              {recommendations.length > 0 && (
                <div className="border-t border-border-muted pt-4">
                  <h4 className="font-semibold text-accent mb-3">Priority Improvements:</h4>
                  <ul className="space-y-2">
                    {recommendations.map((rec, i) => (
                      <li key={i} className="text-xs text-foreground-secondary">• {rec}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Save to Command Center */}
              {commandCenterEnabled && !saved && (
                <div className="border-t border-border-muted pt-4 mt-4">
                  <button
                    onClick={handleSaveToCommandCenter}
                    disabled={saving}
                    className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-500 transition-colors disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Save to Command Center'}
                  </button>
                </div>
              )}
              {saved && (
                <div className="border-t border-border-muted pt-4 mt-4">
                  <div className="w-full px-4 py-3 bg-green-600/20 text-green-400 rounded-lg font-medium flex items-center justify-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Saved to Command Center
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-8 bg-surface rounded-lg border border-border p-6">
          <h3 className="text-lg font-bold text-foreground mb-4">Maturity Level Definitions</h3>
          <div className="grid md:grid-cols-5 gap-4">
            {[1, 2, 3, 4, 5].map(level => {
              const labels = ['Initial', 'Developing', 'Defined', 'Managed', 'Optimizing'];
              const descriptions = [
                'Ad-hoc processes, reactive',
                'Basic practices in place',
                'Standardized and documented',
                'Measured and controlled',
                'Continuous improvement culture',
              ];
              return (
                <div key={level} className="p-4 bg-background rounded border border-border-muted">
                  <div className="text-2xl font-bold text-accent mb-2">Level {level}</div>
                  <div className="font-semibold text-foreground mb-1 text-sm">{labels[level - 1]}</div>
                  <div className="text-xs text-foreground-secondary">{descriptions[level - 1]}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
